import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Select } from "../../components/ui/Select";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { Modal } from "../../components/ui/Modal";
import { Input } from "../../components/ui/Input";
import { useApi } from "../../context/ApiContext";
import { useStore } from "../../context/StoreContext";
import { customizationAssetApi, CustomizationAsset } from "../../services/customizationAssetApi";
import { galleryApi, GalleryImage, bucketApi, BucketFile } from "../../services/galleryApi";
import { getImageUrl } from "../../utils/imageUtils";
import { FaTrash, FaEdit, FaPlus, FaFont, FaImages, FaTags, FaSearch } from "react-icons/fa";
import { MultiSelect } from "../../components/ui/MultiSelect";
import { Pagination } from "../../components/ui/Pagination";

// Helper function to get all available fonts for the designer
const getAllAvailableFonts = (): Array<{ value: string; label: string }> => {
  const systemFonts = [
    "Arial", "Helvetica", "Times New Roman", "Courier New", "Verdana", 
    "Georgia", "Palatino", "Garamond", "Comic Sans MS", "Trebuchet MS", 
    "Impact", "Lucida Console", "Tahoma"
  ];
  
  const typefaceFonts = [
    "Almarai", "Anton", "Barlow Condensed", "BBH Bartle", "Cairo", "Caveat",
    "Dancing Script", "Edu SA Beginner", "Follow", "Great Vibes", "Josefin Sans Thin",
    "Lobster", "Lobster Two", "Noto Sans Symbols", "Orbitron", "Pacifico",
    "Satisfy", "Shadows Into Light", "Smooch Sans Thin", "Space Grotesk Light", "Tajawal"
  ];
  
  const googleFonts = [
    "Inter", "Roboto", "Open Sans", "Lato", "Montserrat", "Oswald", "Raleway",
    "Poppins", "Source Sans Pro", "Playfair Display", "Merriweather", "Ubuntu",
    "Dancing Script", "Pacifico", "Lobster", "Bebas Neue", "Bangers", "Righteous",
    "Fredoka One", "Comfortaa", "Quicksand", "Caveat", "Kalam", "Permanent Marker",
    "Shadows Into Light", "Indie Flower", "Crimson Text", "Fira Sans", "PT Sans",
    "Nunito", "Work Sans", "Rubik", "Noto Sans", "Mukta", "DM Sans", "Manrope",
    "Outfit", "Plus Jakarta Sans", "Trirong",
    // Arabic Fonts
    "Amiri", "Changa", "El Messiri", "Harmattan", "Lateef", "Markazi Text",
    "Reem Kufi", "Scheherazade New", "Noto Sans Arabic", "Noto Naskh Arabic",
    "Noto Kufi Arabic", "IBM Plex Sans Arabic", "Cairo", "Tajawal", "Almarai"
  ];
  
  // Combine all fonts and remove duplicates
  const allFontsSet = new Set([...systemFonts, ...typefaceFonts, ...googleFonts]);
  const allFonts = Array.from(allFontsSet);
  
  return allFonts.map(font => ({ value: font, label: font })).sort((a, b) => 
    a.label.localeCompare(b.label)
  );
};

type TabType = "gallery" | "customization-assets" | "fonts";

export const Gallery: React.FC = () => {
  const { t } = useTranslation();
  const { api } = useApi();
  const { store, refreshStore, isPluginEnabled } = useStore();
  const isCustomizationPluginEnabled = isPluginEnabled("product-customization");
  
  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>("gallery");
  
  // Gallery images state
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryError, setGalleryError] = useState<string | null>(null);
  const [galleryFilterType, setGalleryFilterType] = useState<"all" | "catalog" | "static">("all");
  const [gallerySearchQuery, setGallerySearchQuery] = useState("");
  const [galleryCurrentPage, setGalleryCurrentPage] = useState(1);
  const galleryItemsPerPage = 20;
  
  // Bucket files state
  const [bucketFiles, setBucketFiles] = useState<BucketFile[]>([]);
  const [bucketLoading, setBucketLoading] = useState(false);
  const [bucketError, setBucketError] = useState<string | null>(null);
  
  // Customization assets state
  const [assets, setAssets] = useState<CustomizationAsset[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetsError, setAssetsError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<"all" | "background" | "shape">("all");
  const [assetsSearchQuery, setAssetsSearchQuery] = useState("");
  const [assetsCurrentPage, setAssetsCurrentPage] = useState(1);
  const assetsItemsPerPage = 20;
  
  // Fonts state
  const [allowedDesignerFonts, setAllowedDesignerFonts] = useState<string[]>([]);
  const [savingFonts, setSavingFonts] = useState(false);
  
  // Common state
  const [success, setSuccess] = useState<string | null>(null);
  
  // Gallery upload modal
  const [showGalleryUploadModal, setShowGalleryUploadModal] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [galleryUploadFile, setGalleryUploadFile] = useState<File | null>(null);
  const [galleryUploadName, setGalleryUploadName] = useState("");
  const [galleryUploadType, setGalleryUploadType] = useState<"catalog" | "static">("catalog");
  
  // Gallery edit modal
  const [showGalleryEditModal, setShowGalleryEditModal] = useState(false);
  const [editingGalleryImage, setEditingGalleryImage] = useState<GalleryImage | null>(null);
  const [editingBucketFile, setEditingBucketFile] = useState<BucketFile | null>(null);
  const [editGalleryName, setEditGalleryName] = useState("");
  const [editGalleryType, setEditGalleryType] = useState<"catalog" | "static">("catalog");
  
  // Customization assets modals (from original component)
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState<CustomizationAsset | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState("");
  const [uploadType, setUploadType] = useState<"background" | "shape">("background");
  const [uploadCategory, setUploadCategory] = useState("");

  // Fetch gallery images
  const fetchGalleryImages = useCallback(async () => {
    try {
      setGalleryLoading(true);
      setGalleryError(null);
      const type = galleryFilterType === "all" ? undefined : (galleryFilterType as "catalog" | "static" | undefined);
      const data = await galleryApi.getImages(type, true); // Include usage info
      setGalleryImages(data);
    } catch (err: any) {
      setGalleryError(err.response?.data?.error || "Failed to fetch gallery images");
    } finally {
      setGalleryLoading(false);
    }
  }, [galleryFilterType]);

  // Fetch bucket files
  const fetchBucketFiles = useCallback(async () => {
    try {
      setBucketLoading(true);
      setBucketError(null);
      const data = await bucketApi.listFiles();
      setBucketFiles(data);
    } catch (err: any) {
      setBucketError(err.response?.data?.error || "Failed to fetch bucket files");
    } finally {
      setBucketLoading(false);
    }
  }, []);

  // Fetch customization assets
  const fetchAssets = useCallback(async () => {
    if (!isCustomizationPluginEnabled) {
      return;
    }
    
    try {
      setAssetsLoading(true);
      setAssetsError(null);
      const type = filterType === "all" ? undefined : (filterType as "background" | "shape" | undefined);
      const data = await customizationAssetApi.getAssets(type);
      setAssets(data);
    } catch (err: any) {
      setAssetsError(err.response?.data?.error || "Failed to fetch assets");
    } finally {
      setAssetsLoading(false);
    }
  }, [filterType, isCustomizationPluginEnabled]);

  useEffect(() => {
    if (activeTab === "gallery") {
      // Only fetch bucket files - gallery images are just metadata for bucket files
      fetchBucketFiles();
      // Still fetch gallery images to get names/metadata for bucket files
      fetchGalleryImages();
      // Reset pagination when switching tabs
      setGalleryCurrentPage(1);
      setGallerySearchQuery("");
    } else if (activeTab === "customization-assets") {
      fetchAssets();
      // Reset pagination when switching tabs
      setAssetsCurrentPage(1);
      setAssetsSearchQuery("");
    }
  }, [activeTab, fetchGalleryImages, fetchBucketFiles, fetchAssets]);

  // Filter and paginate bucket files
  const filteredBucketFiles = useMemo(() => {
    return bucketFiles.filter((file: BucketFile) => {
      const galleryImage = galleryImages.find((img) => img.url === file.url);
      const displayName = galleryImage?.name || file.key.split("/").pop() || file.key;
      const searchLower = gallerySearchQuery.toLowerCase();
      return displayName.toLowerCase().includes(searchLower) ||
             file.key.toLowerCase().includes(searchLower);
    });
  }, [bucketFiles, galleryImages, gallerySearchQuery]);

  const galleryTotalPages = Math.max(1, Math.ceil(filteredBucketFiles.length / galleryItemsPerPage));
  const galleryStartIndex = (galleryCurrentPage - 1) * galleryItemsPerPage;
  const galleryEndIndex = galleryStartIndex + galleryItemsPerPage;
  const paginatedBucketFiles = filteredBucketFiles.slice(galleryStartIndex, galleryEndIndex);

  // Filter and paginate customization assets
  const filteredAssets = useMemo(() => {
    return assets.filter((asset: CustomizationAsset) => {
      const searchLower = assetsSearchQuery.toLowerCase();
      return asset.name.toLowerCase().includes(searchLower) ||
             (asset.category && asset.category.toLowerCase().includes(searchLower));
    });
  }, [assets, assetsSearchQuery]);

  const assetsTotalPages = Math.max(1, Math.ceil(filteredAssets.length / assetsItemsPerPage));
  const assetsStartIndex = (assetsCurrentPage - 1) * assetsItemsPerPage;
  const assetsEndIndex = assetsStartIndex + assetsItemsPerPage;
  const paginatedAssets = filteredAssets.slice(assetsStartIndex, assetsEndIndex);

  // Load allowed fonts from store
  useEffect(() => {
    if (activeTab === "fonts" && store?.allowedDesignerFonts) {
      setAllowedDesignerFonts(store.allowedDesignerFonts);
    }
  }, [activeTab, store?.allowedDesignerFonts]);

  // Gallery image handlers
  const handleGalleryUpload = async () => {
    if (!galleryUploadFile || !galleryUploadName.trim()) {
      setGalleryError("Please provide both file and name");
      return;
    }

    try {
      setGalleryUploading(true);
      setGalleryError(null);
      await galleryApi.uploadImage({
        file: galleryUploadFile,
        name: galleryUploadName.trim(),
        type: galleryUploadType,
      });
      setSuccess("Gallery image uploaded successfully");
      setShowGalleryUploadModal(false);
      setGalleryUploadFile(null);
      setGalleryUploadName("");
      await fetchGalleryImages();
      await fetchBucketFiles();
    } catch (err: any) {
      setGalleryError(err.response?.data?.error || "Failed to upload gallery image");
    } finally {
      setGalleryUploading(false);
    }
  };

  const handleGalleryEdit = async () => {
    if (!editGalleryName.trim()) {
      setGalleryError("Name is required");
      return;
    }

    try {
      setGalleryUploading(true);
      setGalleryError(null);

      if (editingBucketFile && !editingGalleryImage) {
        // Creating a new gallery image entry for an existing bucket file
        await galleryApi.createImage({
          name: editGalleryName.trim(),
          url: editingBucketFile.url,
          thumbnailUrl: editingBucketFile.thumbnailUrl,
          type: editGalleryType,
        });
        setSuccess("Image named successfully");
      } else if (editingGalleryImage) {
        // Updating existing gallery image
        await galleryApi.updateImage(editingGalleryImage._id, {
          name: editGalleryName.trim(),
          type: editGalleryType,
        });
        setSuccess("Gallery image updated successfully");
      }

      setShowGalleryEditModal(false);
      setEditingGalleryImage(null);
      setEditingBucketFile(null);
      await fetchGalleryImages();
      await fetchBucketFiles();
    } catch (err: any) {
      setGalleryError(err.response?.data?.error || "Failed to save image");
    } finally {
      setGalleryUploading(false);
    }
  };

  const handleGalleryDelete = async (id: string) => {
    if (!window.confirm(t("admin.confirmDelete"))) {
      return;
    }

    try {
      setGalleryError(null);
      await galleryApi.deleteImage(id);
      setSuccess("Image name removed successfully");
      await fetchGalleryImages();
      await fetchBucketFiles();
    } catch (err: any) {
      setGalleryError(err.response?.data?.error || "Failed to remove image name");
    }
  };

  const handleBucketFileDelete = async (file: BucketFile) => {
    if (!window.confirm(t("admin.confirmDelete"))) {
      return;
    }

    try {
      setBucketError(null);
      await bucketApi.deleteFile(file.url);
      setSuccess("File deleted successfully");
      await fetchBucketFiles();
    } catch (err: any) {
      setBucketError(err.response?.data?.error || "Failed to delete file");
    }
  };

  // Customization assets handlers (from original component)
  const handleAssetUpload = async () => {
    if (!uploadFile) {
      setAssetsError("Please select a file");
      return;
    }

    try {
      setUploading(true);
      setAssetsError(null);
      await customizationAssetApi.uploadAsset({
        file: uploadFile,
        type: uploadType,
        name: uploadName || uploadFile.name,
        category: uploadCategory || undefined,
      });
      setSuccess("Asset uploaded successfully");
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadName("");
      setUploadCategory("");
      await fetchAssets();
    } catch (err: any) {
      setAssetsError(err.response?.data?.error || "Failed to upload asset");
    } finally {
      setUploading(false);
    }
  };

  const handleAssetEdit = async () => {
    if (!editingAsset) return;

    try {
      setUploading(true);
      setAssetsError(null);
      await customizationAssetApi.updateAsset(editingAsset._id, {
        name: uploadName,
        category: uploadCategory || undefined,
        isActive: editingAsset.isActive,
      });
      setSuccess("Asset updated successfully");
      setShowEditModal(false);
      setEditingAsset(null);
      setUploadName("");
      setUploadCategory("");
      await fetchAssets();
    } catch (err: any) {
      setAssetsError(err.response?.data?.error || "Failed to update asset");
    } finally {
      setUploading(false);
    }
  };

  const handleAssetDelete = async (id: string) => {
    if (!window.confirm(t("admin.confirmDelete"))) {
      return;
    }

    try {
      setAssetsError(null);
      await customizationAssetApi.deleteAsset(id);
      setSuccess("Asset deleted successfully");
      await fetchAssets();
    } catch (err: any) {
      setAssetsError(err.response?.data?.error || "Failed to delete asset");
    }
  };

  // Font handlers
  const handleSaveFonts = async () => {
    try {
      setSavingFonts(true);
      setGalleryError(null);
      await api.put("/store/allowed-designer-fonts", { allowedDesignerFonts });
      setSuccess("Fonts saved successfully");
      await refreshStore();
    } catch (err: any) {
      setGalleryError(err.response?.data?.error || "Failed to save fonts");
    } finally {
      setSavingFonts(false);
    }
  };

  // Open edit modals
  const openGalleryEditModal = (image: GalleryImage) => {
    setEditingGalleryImage(image);
    setEditGalleryName(image.name);
    setEditGalleryType(image.type);
    setShowGalleryEditModal(true);
  };

  const openBucketFileNameModal = (file: BucketFile) => {
    // Find if there's already a GalleryImage entry for this file
    const existingImage = galleryImages.find((img) => img.url === file.url);
    if (existingImage) {
      openGalleryEditModal(existingImage);
    } else {
      // Create a new modal state for naming
      setEditingBucketFile(file);
      setEditGalleryName(file.key.split("/").pop() || "");
      setEditGalleryType("catalog"); // Default to catalog
      setShowGalleryEditModal(true);
    }
  };

  const openAssetEditModal = (asset: CustomizationAsset) => {
    setEditingAsset(asset);
    setUploadName(asset.name);
    setUploadCategory(asset.category || "");
    setUploadType(asset.type);
    setShowEditModal(true);
  };

  return (
    <Container>
      <Stack spacing="lg">

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 dark:text-white">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab("gallery")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "gallery"
                  ? "border-blue-500 text-blue-600 dark:text-blue-400 dark:border-blue-400"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
              }`}
            >
              <FaImages className="inline mr-2" />
              {t("admin.gallery.images")}
            </button>
            {isCustomizationPluginEnabled && (
              <>
                <button
                  onClick={() => setActiveTab("customization-assets")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "customization-assets"
                      ? "border-blue-500 text-blue-600 dark:text-blue-400"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                  }`}
                >
                  <FaTags className="inline mr-2" />
                  {t("admin.sidebar.customizationAssets")}
                </button>
                <button
                  onClick={() => setActiveTab("fonts")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "fonts"
                      ? "border-blue-500 text-blue-600 dark:text-blue-400"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                  }`}
                >
                  <FaFont className="inline mr-2" />
                  {t("admin.gallery.fonts")}
                </button>
              </>
            )}
          </nav>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <Alert variant="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        {(galleryError || assetsError || bucketError) && (
          <Alert variant="error" onClose={() => { setGalleryError(null); setAssetsError(null); setBucketError(null); }}>
            {galleryError || assetsError || bucketError}
          </Alert>
        )}

        {/* Gallery Images Tab (includes both Gallery Images and Bucket Files) */}
        {activeTab === "gallery" && (
          <Card>
            <Stack spacing="md">
              <div className="flex flex-col gap-4">
                <div className="flex justify-between items-center">
                  <Heading size="md">{t("admin.gallery.images")}</Heading>
                  <div className="flex gap-2 items-center">
                    <Select
                      value={galleryFilterType}
                      onChange={(e) => {
                        setGalleryFilterType(e.target.value as "all" | "catalog" | "static");
                        setGalleryCurrentPage(1);
                      }}
                      options={[
                        { value: "all", label: t("admin.gallery.allTypes") },
                        { value: "catalog", label: t("admin.gallery.catalog") },
                        { value: "static", label: t("admin.gallery.static") },
                      ]}
                      searchable={false}
                      className="min-w-[150px]"
                    />
                    <Button onClick={() => setShowGalleryUploadModal(true)} size="md">
                      <FaPlus className="mr-2" />
                      {t("admin.gallery.addImage")}
                    </Button>
                    <Button onClick={() => { fetchGalleryImages(); fetchBucketFiles(); }} variant="outline" size="md">
                      {t("admin.refresh")}
                    </Button>
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  <div className="relative flex-1 max-w-md">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <Input
                      type="text"
                      placeholder={t("admin.gallery.searchPlaceholder")}
                      value={gallerySearchQuery}
                      onChange={(e) => {
                        setGallerySearchQuery(e.target.value);
                        setGalleryCurrentPage(1);
                      }}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              {(galleryLoading || bucketLoading) ? (
                <Spinner />
              ) : filteredBucketFiles.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400">{t("admin.gallery.noImages")}</p>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {/* Bucket Files - only show bucket files, use gallery images for names/metadata */}
                    {paginatedBucketFiles.map((file) => {
                      // Find corresponding gallery image entry if it exists
                      const galleryImage = galleryImages.find((img) => img.url === file.url);
                      const displayName = galleryImage?.name || file.key.split("/").pop() || file.key;
                      const imageType = galleryImage?.type;

                    const isVideo = file.contentType?.startsWith("video/") || 
                                    file.key.toLowerCase().match(/\.(mp4|webm|mov|avi|mkv)$/);
                    
                    return (
                      <div key={`bucket-${file.key}`} className="border rounded-lg p-4 dark:border-gray-700">
                        {isVideo ? (
                          <div className="relative w-full h-32 bg-black dark:bg-gray-900 rounded mb-2 overflow-hidden">
                            <video
                              src={getImageUrl(file.url)}
                              poster={file.thumbnailUrl ? getImageUrl(file.thumbnailUrl) : undefined}
                              className="w-full h-full object-contain"
                              controls
                              preload="metadata"
                              playsInline
                              muted={false}
                              style={{ maxHeight: "128px" }}
                            >
                              {t("admin.gallery.videoNotSupported")}
                            </video>
                          </div>
                        ) : file.thumbnailUrl ? (
                          <img
                            src={getImageUrl(file.thumbnailUrl)}
                            alt={displayName}
                            className="w-full h-32 object-cover rounded mb-2"
                          />
                        ) : (
                          <img
                            src={getImageUrl(file.url)}
                            alt={displayName}
                            className="w-full h-32 object-cover rounded mb-2"
                          />
                        )}
                        <div className="text-sm font-medium mb-1 truncate" title={displayName}>
                          {displayName}
                        </div>
                        {imageType && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                            {imageType === "catalog" ? t("admin.gallery.catalog") : t("admin.gallery.static")}
                          </div>
                        )}
                        {file.size && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                            {(file.size / 1024).toFixed(1)} KB
                          </div>
                        )}
                        {file.isUsed && (
                          <div className="text-xs text-orange-600 dark:text-orange-400 mb-2">
                            {t("admin.gallery.used", {
                              products: file.usedInProducts,
                              categories: file.usedInCategories,
                            })} {file.usedInAssets > 0 && `, ${file.usedInAssets} asset(s)`}
                          </div>
                        )}
                        {!file.isUsed && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                            {t("admin.gallery.notUsed")}
                          </div>
                        )}
                        <div className="flex gap-2">
                          <Button
                            onClick={() => openBucketFileNameModal(file)}
                            variant="ghost"
                            size="sm"
                            title={galleryImage ? t("admin.gallery.editName") : t("admin.gallery.nameImage")}
                            className="p-2"
                          >
                            <FaEdit className="w-4 h-4" />
                          </Button>
                          {galleryImage ? (
                            <Button
                              onClick={() => handleGalleryDelete(galleryImage._id)}
                              variant="ghost"
                              size="sm"
                              title={t("admin.gallery.removeName")}
                              className="p-2 text-orange-600 hover:text-orange-700 dark:text-orange-400"
                            >
                              <FaTrash className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button
                              onClick={() => handleBucketFileDelete(file)}
                              disabled={file.isUsed}
                              variant="ghost"
                              size="sm"
                              title={file.isUsed ? t("admin.gallery.cannotDelete") : ""}
                              className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 disabled:text-gray-400 disabled:cursor-not-allowed"
                            >
                              <FaTrash className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  </div>
                  {galleryTotalPages > 1 && (
                    <div className="mt-4">
                      <Pagination
                        currentPage={galleryCurrentPage}
                        totalPages={galleryTotalPages}
                        onPageChange={setGalleryCurrentPage}
                      />
                    </div>
                  )}
                  <div className="text-sm text-gray-500 dark:text-gray-400 text-center mt-2">
                    {t("admin.gallery.showingResults", {
                      start: galleryStartIndex + 1,
                      end: Math.min(galleryEndIndex, filteredBucketFiles.length),
                      total: filteredBucketFiles.length,
                    })}
                  </div>
                </>
              )}
            </Stack>
          </Card>
        )}

        {/* Customization Assets Tab */}
        {activeTab === "customization-assets" && isCustomizationPluginEnabled && (
          <Card>
            <Stack spacing="md">
              <div className="flex flex-col gap-4">
                <div className="flex justify-between items-center">
                  <div className="flex gap-2 items-center">
                    <Select
                      value={filterType}
                      onChange={(e) => {
                        setFilterType(e.target.value as "all" | "background" | "shape");
                        setAssetsCurrentPage(1);
                      }}
                      options={[
                        { value: "all", label: t("admin.gallery.all") },
                        { value: "background", label: t("admin.gallery.backgrounds") },
                        { value: "shape", label: t("admin.gallery.shapes") },
                      ]}
                      searchable={false}
                      className="min-w-[150px]"
                    />
                    <Button onClick={() => setShowUploadModal(true)} size="md">
                      <FaPlus className="mr-2" />
                      {t("admin.gallery.addAsset")}
                    </Button>
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  <div className="relative flex-1 max-w-md">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <Input
                      type="text"
                      placeholder={t("admin.gallery.searchPlaceholder")}
                      value={assetsSearchQuery}
                      onChange={(e) => {
                        setAssetsSearchQuery(e.target.value);
                        setAssetsCurrentPage(1);
                      }}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              {assetsLoading ? (
                <Spinner />
              ) : filteredAssets.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400">{t("admin.gallery.noAssets")}</p>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {paginatedAssets.map((asset) => (
                    <div key={asset._id} className="border rounded-lg p-4 dark:border-gray-700">
                      <img
                        src={getImageUrl(asset.thumbnailUrl || asset.url)}
                        alt={asset.name}
                        className="w-full h-32 object-cover rounded mb-2"
                      />
                      <div className="text-sm font-medium mb-1">{asset.name}</div>
                      {asset.category && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                          {asset.category}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          onClick={() => openAssetEditModal(asset)}
                          variant="ghost"
                          size="sm"
                          className="p-2"
                        >
                          <FaEdit className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleAssetDelete(asset._id)}
                          variant="ghost"
                          size="sm"
                          className="p-2 text-red-600 hover:text-red-700 dark:text-red-400"
                        >
                          <FaTrash className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    ))}
                  </div>
                  {assetsTotalPages > 1 && (
                    <div className="mt-4">
                      <Pagination
                        currentPage={assetsCurrentPage}
                        totalPages={assetsTotalPages}
                        onPageChange={setAssetsCurrentPage}
                      />
                    </div>
                  )}
                  <div className="text-sm text-gray-500 dark:text-gray-400 text-center mt-2">
                    {t("admin.gallery.showingResults", {
                      start: assetsStartIndex + 1,
                      end: Math.min(assetsEndIndex, filteredAssets.length),
                      total: filteredAssets.length,
                    })}
                  </div>
                </>
              )}
            </Stack>
          </Card>
        )}

        {/* Fonts Tab */}
        {activeTab === "fonts" && isCustomizationPluginEnabled && (
          <Card>
            <Stack spacing="md">
              <Heading size="md">{t("admin.gallery.fonts")}</Heading>
              <MultiSelect
                options={getAllAvailableFonts()}
                value={allowedDesignerFonts}
                onChange={setAllowedDesignerFonts}
                placeholder={t("admin.gallery.selectFonts")}
              />
              <Button onClick={handleSaveFonts} disabled={savingFonts}>
                {savingFonts ? t("admin.saving") : t("admin.save")}
              </Button>
            </Stack>
          </Card>
        )}

        {/* Gallery Upload Modal */}
        <Modal
          isOpen={showGalleryUploadModal}
          onClose={() => {
            setShowGalleryUploadModal(false);
            setGalleryUploadFile(null);
            setGalleryUploadName("");
          }}
          title={t("admin.gallery.uploadImage")}
        >
          <Stack spacing="md">
            <div>
              <label className="block text-sm font-medium mb-1">{t("admin.gallery.imageFile")}</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setGalleryUploadFile(e.target.files?.[0] || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t("admin.gallery.imageName")}</label>
              <Input
                value={galleryUploadName}
                onChange={(e) => setGalleryUploadName(e.target.value)}
                placeholder={t("admin.gallery.imageNamePlaceholder")}
              />
            </div>
            <div>
              <Select
                label={t("admin.gallery.imageType")}
                value={galleryUploadType}
                onChange={(e) => setGalleryUploadType(e.target.value as "catalog" | "static")}
                options={[
                  { value: "catalog", label: `${t("admin.gallery.catalog")} (${t("admin.gallery.catalogDescription")})` },
                  { value: "static", label: t("admin.gallery.static") },
                ]}
                searchable={false}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowGalleryUploadModal(false);
                  setGalleryUploadFile(null);
                  setGalleryUploadName("");
                }}
              >
                {t("admin.cancel")}
              </Button>
              <Button onClick={handleGalleryUpload} disabled={galleryUploading || !galleryUploadFile || !galleryUploadName.trim()}>
                {galleryUploading ? t("admin.uploading") : t("admin.upload")}
              </Button>
            </div>
          </Stack>
        </Modal>

        {/* Gallery Edit Modal */}
        <Modal
          isOpen={showGalleryEditModal}
          onClose={() => {
            setShowGalleryEditModal(false);
            setEditingGalleryImage(null);
            setEditingBucketFile(null);
          }}
          title={editingBucketFile && !editingGalleryImage ? t("admin.gallery.nameImage") : t("admin.gallery.editImage")}
        >
          <Stack spacing="md">
            <div>
              <Input
                label={t("admin.gallery.imageName")}
                value={editGalleryName}
                onChange={(e) => setEditGalleryName(e.target.value)}
                placeholder={t("admin.gallery.imageNamePlaceholder")}
              />
            </div>
            {(!editingBucketFile || editingGalleryImage) && (
              <div>
                <Select
                  label={t("admin.gallery.imageType")}
                  value={editGalleryType}
                  onChange={(e) => setEditGalleryType(e.target.value as "catalog" | "static")}
                  options={[
                    { value: "catalog", label: t("admin.gallery.catalog") },
                    { value: "static", label: t("admin.gallery.static") },
                  ]}
                  searchable={false}
                />
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowGalleryEditModal(false);
                  setEditingGalleryImage(null);
                  setEditingBucketFile(null);
                }}
              >
                {t("admin.cancel")}
              </Button>
              <Button onClick={handleGalleryEdit} disabled={galleryUploading || !editGalleryName.trim()}>
                {galleryUploading ? t("admin.saving") : t("admin.save")}
              </Button>
            </div>
          </Stack>
        </Modal>

        {/* Asset Upload Modal (from original) */}
        <Modal
          isOpen={showUploadModal}
          onClose={() => {
            setShowUploadModal(false);
            setUploadFile(null);
            setUploadName("");
            setUploadCategory("");
          }}
          title={t("admin.gallery.uploadAsset")}
        >
          <Stack spacing="md">
            <div>
              <label className="block text-sm font-medium mb-1">{t("admin.gallery.assetFile")}</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              />
            </div>
            <div>
              <Input
                label={t("admin.customizationAssets.nameOptional")}
                value={uploadName}
                onChange={(e) => setUploadName(e.target.value)}
              />
            </div>
            <div>
              <Select
                label={t("admin.gallery.assetType")}
                value={uploadType}
                onChange={(e) => setUploadType(e.target.value as "background" | "shape")}
                options={[
                  { value: "background", label: t("admin.gallery.background") },
                  { value: "shape", label: t("admin.gallery.shape") },
                ]}
                searchable={false}
              />
            </div>
            {uploadType === "shape" && (
              <div>
                <label className="block text-sm font-medium mb-1">{t("admin.customizationAssets.categoryOptional")}</label>
                <Input
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value)}
                />
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFile(null);
                  setUploadName("");
                  setUploadCategory("");
                }}
              >
                {t("admin.cancel")}
              </Button>
              <Button onClick={handleAssetUpload} disabled={uploading || !uploadFile}>
                {uploading ? t("admin.uploading") : t("admin.upload")}
              </Button>
            </div>
          </Stack>
        </Modal>

        {/* Asset Edit Modal (from original) */}
        <Modal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingAsset(null);
            setUploadName("");
            setUploadCategory("");
          }}
          title={t("admin.gallery.editAsset")}
        >
          <Stack spacing="md">
            <div>
              <label className="block text-sm font-medium mb-1">{t("admin.customizationAssets.nameOptional")}</label>
              <Input
                value={uploadName}
                onChange={(e) => setUploadName(e.target.value)}
              />
            </div>
            {editingAsset?.type === "shape" && (
              <div>
                <label className="block text-sm font-medium mb-1">{t("admin.customizationAssets.categoryOptional")}</label>
                <Input
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value)}
                />
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingAsset(null);
                  setUploadName("");
                  setUploadCategory("");
                }}
              >
                {t("admin.cancel")}
              </Button>
              <Button onClick={handleAssetEdit} disabled={uploading}>
                {uploading ? t("admin.saving") : t("admin.save")}
              </Button>
            </div>
          </Stack>
        </Modal>
      </Stack>
    </Container>
  );
};
